import apiClient from './client';
import { CustomerAllocation, LedgerEntry, CustomerDeposit, Check } from '../types';

export interface CustomerBalance {
  customer_id: string;
  customer_name: string;
  balance: number;
  unallocated_deposits: number;
  transaction_count: number;
}

export interface SuggestedDeposit {
  id: string;
  customer_id?: string;
  customer_name?: string;
  deposit_date?: string;
  amount?: number;
}

export interface LedgerEntrySummary {
  id: string;
  entry_date?: string;
  amount?: number;
  entry_type?: string;
  description?: string;
}

export interface AllocationSuggestion {
  ledger_entry_id: string;
  ledger_entry: LedgerEntrySummary;
  suggested_deposits: SuggestedDeposit[];
  total_amount: number;
  difference: number;
}

export interface AllocationEngineResult {
  allocations_created: number;
  suggestions: AllocationSuggestion[];
  total_suggestions: number;
  total_amount: number;
  processing_time_ms: number;
}

export interface AllocationParams {
  amount_tolerance?: number;
  date_window?: number;
  max_cluster_size?: number;
  account_ids?: string[];
}

export interface IncompletePurchase {
  id: string;
  org_id: string;
  client_id: string;
  ledger_entry_id: string;
  customer_id: string;
  amount: number;
  allocation_source: string | null;
  notes: string | null;
  created_at: string;
  customer_name: string | null;
  ledger_entry_date: string | null;
  ledger_entry_amount: number | null;
  ledger_entry_description: string | null;
  check_id: string;
  check_number: string | null;
  check_date: string | null;
  check_amount: number | null;
  payee_name: string | null;
}

export const allocationsApi = {
  /**
   * Run the allocation engine for a client
   */
  runAllocationEngine: async (clientId: string, params?: AllocationParams): Promise<AllocationEngineResult> => {
    const response = await apiClient.post<AllocationEngineResult>(`/clients/${clientId}/allocations/run-engine`, params);
    return response.data;
  },

  /**
   * Get allocation suggestions
   */
  getAllocationSuggestions: async (
    clientId: string
  ): Promise<AllocationSuggestion[]> => {
    const response = await apiClient.get<AllocationSuggestion[]>(
      `/clients/${clientId}/allocations/suggestions`
    );
    return response.data;
  },

  /**
   * Accept allocation suggestions (new format with deposit clusters)
   */
  acceptAllocationSuggestions: async (clientId: string, suggestions: AllocationSuggestion[]): Promise<void> => {
    await apiClient.post(`/clients/${clientId}/allocations/engine/accept`, { suggestions });
  },

  /**
   * Create a manual allocation
   * For deposits: requires customer_id and optionally deposit_id
   * For purchases: requires check_id, customer_id is optional (will use check's customer_id if available)
   */
  createManualAllocation: async (data: {
    client_id: string;
    customer_id?: string;
    ledger_entry_id?: string;
    deposit_id?: string;
    check_id?: string;
    amount: number;
    notes?: string;
  }): Promise<CustomerAllocation> => {
    const { client_id, ...requestData } = data;
    const response = await apiClient.post<CustomerAllocation>(
      `/clients/${client_id}/allocations/manual-match`,
      requestData
    );
    return response.data;
  },

  /**
   * Get all allocations for a customer
   */
  getCustomerAllocations: async (clientId: string, customerId: string): Promise<CustomerAllocation[]> => {
    const response = await apiClient.get<CustomerAllocation[]>(
      `/clients/${clientId}/customers/${customerId}/allocations`
    );
    return response.data;
  },

  /**
   * Get all allocations for a client
   */
  getClientAllocations: async (clientId: string, options?: { accountId?: string | null }): Promise<CustomerAllocation[]> => {
    const params = new URLSearchParams();
    if (options?.accountId) {
      params.append('account_id', options.accountId);
    }
    const queryString = params.toString();
    const response = await apiClient.get<CustomerAllocation[]>(
      `/clients/${clientId}/allocations${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },

  /**
   * Delete an allocation
   */
  deleteAllocation: async (clientId: string, allocationId: string): Promise<void> => {
    await apiClient.delete(`/clients/${clientId}/allocations/${allocationId}`);
  },

  /**
   * Get customer balances
   */
  getCustomerBalances: async (clientId: string, options?: { accountId?: string | null }): Promise<CustomerBalance[]> => {
    const params = new URLSearchParams();
    if (options?.accountId) {
      params.append('account_id', options.accountId);
    }
    const queryString = params.toString();
    const response = await apiClient.get<CustomerBalance[]>(
      `/clients/${clientId}/customer-balances${queryString ? `?${queryString}` : ''}`
    );
    return response.data;
  },

  /**
   * Get ledger entry for an allocation (for row expansion)
   */
  getAllocationLedgerEntry: async (clientId: string, allocationId: string): Promise<LedgerEntry> => {
    const response = await apiClient.get<LedgerEntry>(
      `/clients/${clientId}/allocations/${allocationId}/ledger-entry`
    );
    return response.data;
  },

  /**
   * Get deposits for an allocation (for row expansion)
   */
  getAllocationDeposits: async (clientId: string, allocationId: string): Promise<CustomerDeposit[]> => {
    const response = await apiClient.get<CustomerDeposit[]>(
      `/clients/${clientId}/allocations/${allocationId}/deposits`
    );
    return response.data;
  },

  /**
   * Get checks for an allocation (for row expansion)
   */
  getAllocationChecks: async (clientId: string, allocationId: string): Promise<Check[]> => {
    const response = await apiClient.get<Check[]>(
      `/clients/${clientId}/allocations/${allocationId}/checks`
    );
    return response.data;
  },

  /**
   * Get unassigned deposits for a customer (for row expansion)
   */
  getCustomerUnassignedDeposits: async (clientId: string, customerId: string): Promise<CustomerDeposit[]> => {
    const response = await apiClient.get<CustomerDeposit[]>(
      `/clients/${clientId}/customers/${customerId}/unassigned-deposits`
    );
    return response.data;
  },

  /**
   * Get deposits for a suggestion (for row expansion)
   */
  getSuggestionDeposits: async (ledgerEntryId: string): Promise<CustomerDeposit[]> => {
    const response = await apiClient.get<CustomerDeposit[]>(
      `/allocations/suggestions/${ledgerEntryId}/deposits`
    );
    return response.data;
  },

  /**
   * Get ledger entry for a suggestion (for row expansion)
   */
  getSuggestionLedgerEntry: async (ledgerEntryId: string): Promise<LedgerEntry> => {
    const response = await apiClient.get<LedgerEntry>(
      `/ledger-entries/${ledgerEntryId}`
    );
    return response.data;
  },

  /**
   * Get incomplete purchases (purchases where linked check has no vendor_id)
   */
  getIncompletePurchases: async (clientId: string): Promise<IncompletePurchase[]> => {
    const response = await apiClient.get<IncompletePurchase[]>(
      `/clients/${clientId}/allocations/incomplete-purchases`
    );
    return response.data;
  },
};
